import { Suspense, lazy, useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useAdminAuthController } from './auth/admin-auth-controller';
import {
  initialiseAdminFirebaseApp,
  resolveFirebaseConfiguration,
} from './firebase-config';
import { AdminStateScreen } from './components/AdminExperience';
import { AdminPasswordLogin } from './components/AdminPasswordLogin';

/**
 * The signed-in workspace is loaded on demand.
 *
 * It is the only importer of `@firecms/core`, `@firecms/firebase`, the
 * collections and the data delegate — the bulk of the bundle. An operator
 * staring at a password field has no use for any of it, and before this split
 * they waited for all of it before the form could paint.
 */
const AdminWorkspace = lazy(() => import('./components/AdminWorkspace'));

interface ConfiguredAdminProps {
  firebaseConfig: Record<string, unknown>;
  environmentMode: string;
  projectId: string;
  adminApiBaseUrl: string;
}

function ConfiguredAdmin({
  firebaseConfig,
  environmentMode,
  projectId,
  adminApiBaseUrl,
}: ConfiguredAdminProps) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    void i18n.loadNamespaces(['rtdb']);
  }, [i18n]);

  const firebaseApp = useMemo(
    () => initialiseAdminFirebaseApp(firebaseConfig),
    [firebaseConfig],
  );

  const authController = useAdminAuthController({
    firebaseApp,
    apiBaseUrl: adminApiBaseUrl,
  });

  const getSessionToken = useCallback(
    () => authController.getAuthToken(),
    [authController],
  );
  const handleUnauthorized = useCallback(
    () => authController.invalidateSession(),
    [authController],
  );

  const loadingScreen = (
    <AdminStateScreen title={t('common:states.loading.title')} tone="loading" />
  );

  if (authController.authLoading) {
    return loadingScreen;
  }

  // Rendered outside the FireCMS provider so signing in never has to wait for
  // the workspace chunk.
  if (!authController.user) {
    return <AdminPasswordLogin authController={authController} />;
  }

  return (
    <Suspense fallback={loadingScreen}>
      <AdminWorkspace
        firebaseApp={firebaseApp}
        authController={authController}
        environmentMode={environmentMode}
        projectId={projectId}
        adminApiBaseUrl={adminApiBaseUrl}
        getSessionToken={getSessionToken}
        onUnauthorized={handleUnauthorized}
      />
    </Suspense>
  );
}

export default function App() {
  const { t } = useTranslation();
  const configuration = resolveFirebaseConfiguration(import.meta.env);

  useEffect(() => {
    document.title = t('common:app.title');
  }, [t]);

  if ('error' in configuration) {
    return (
      <AdminStateScreen
        title={t('common:states.configuration.title')}
        description={t('common:states.configuration.description')}
        tone="error"
        detailLabel={t('common:states.configuration.detailLabel')}
        technicalDetails={configuration.missingKeys}
        actionLabel={t('common:actions.reload')}
        onAction={() => window.location.reload()}
      />
    );
  }

  return (
    <ConfiguredAdmin
      firebaseConfig={configuration.config as Record<string, unknown>}
      environmentMode={import.meta.env.MODE}
      projectId={configuration.config?.projectId ?? ''}
      adminApiBaseUrl={configuration.adminApiBaseUrl}
    />
  );
}
